import { globalHistory } from '@reach/router';
import { Link, navigate } from 'gatsby';
import { forwardRef, useEffect, useState } from 'react';
import { secondaryNavLinks } from '../../../../constants/menus';
import { ACCOUNT_ORDERS_PAGE, LOGIN_PAGE } from '../../../../constants/page-links';
import { useAppContext } from '../../../../contexts/AppProvider';
import FacebookIcon from '../../../../images/icon-facebook-alt.inline.svg';
import InstagramIcon from '../../../../images/icon-instagram-alt.inline.svg';
import PinterestIcon from '../../../../images/icon-pinterest.inline.svg';
import TwitterIcon from '../../../../images/icon-new-twitter.svg';
import { classNames } from '../../../../utils/classnames';
import { usePageContext } from '../../../../contexts/PageProvider';

import {
  convertArrayToString,
  convertStringToLocaleLowerCase,
  convertStringToLowercase,
  convertStringToTitleCase
} from '../../../../utils/convertValues';
import { isEmpty } from '../../../../utils/typeCheck';
import { CartDropdown } from '../../../cart-dropdown';

/**
 * @description Render memoized sub header forwarded ref component
 * @param {Object} props
 * @returns {JSX.Element} Memoized sub header forwarded ref component
 */
export const SubHeader = forwardRef(function ForwardedRefSubHeader(props, ref) {
  // const [showCartDropdown, setShowCartDropdown] = useState(false);
  const [cumulativePathSegments, setCumulativePathSegments] = useState([]);
  const pathname = globalHistory.location.pathname;
  const { showCartDropdown, setShowCartDropdown } = usePageContext();
  const { instagramUrl = null, twitterUrl = null, facebookUrl = null, pinterestUrl = null } = props;

  // Translations
  const followUsText = 'Follow us';
  const loginText = 'Login';
  const profileText = 'Profile';
  const followUsOnInstagramText = 'Follow us on Instagram';
  const followUsOnFacebookText = 'Follow us on Facebook';
  const followUsOnTwitterText = 'Follow us on Twitter';
  const followUsOnPinterestText = 'Follow us on Pinterest';

  const appData = useAppContext();

  useEffect(() => {
    const pathData = [];

    pathname
      ?.split('/')
      ?.filter(Boolean)
      ?.reduce(
        (segments, segment) => {
          const parsedSegment = parseInt(segment);

          if (isNaN(parsedSegment)) {
            const previous = segments[segments.length - 1];
            const convertedSegment = convertStringToTitleCase(segment.split('-').join(' '));

            pathData.push({
              path: previous ? `${previous}${segment}/` : `/${segment}`,
              label: convertedSegment
            });

            segments.push(`${previous}${segment}/`);
          }

          return segments;
        },
        ['/']
      );

    setCumulativePathSegments(pathData);
  }, [pathname]);

  const handleSecondaryNavAriaLabel = (menu) => {
    const labels = [];

    if (menu && Array.isArray(menu) && menu?.length > 0) {
      menu.map((item) => labels.push(convertStringToLowercase(item?.label || null)));
    }

    const stringifiedLabels = convertArrayToString(labels);

    return stringifiedLabels;
  };

  // const onExpandedMenuMouseEnter = () =>
  //   ref?.current?.classList?.contains('header--expanded') ? setShowCartDropdown(false) : setShowCartDropdown(true);

  const onExpandedMenuClick = (e, link) => {
    e.preventDefault();

    if (e?.target && ref?.current?.classList?.contains('header--expanded')) {
      ref?.current?.classList?.remove('header--expanded');
    }

    setTimeout(() => {
      navigate(link);
    }, 500);
  };

  // const onExpandedMenuMouseLeave = () => setShowCartDropdown(false);

  return (
    <div className="header__sub">
      <div className="header__sub-inner relative">
        {!isEmpty(secondaryNavLinks) && (
          <div className="secondary-navigation" aria-label={handleSecondaryNavAriaLabel(secondaryNavLinks)}>
            <ul role="list">
              {secondaryNavLinks?.map((item, index) => (
                <li
                  key={item?.url || index}
                  className={classNames(
                    'secondary-navigation__item',
                    !isEmpty(item?.label) && `secondary-navigation--${convertStringToLocaleLowerCase(item?.label)}`
                  )}
                  role="listitem"
                  aria-label={item?.label || ''}
                  onClick={() => {
                    item.label?.toLowerCase() === 'search'
                      ? props.setDisplayMainMenuClass(false)
                      : props.setDisplayMainMenuClass(true);
                  }}
                >
                  {!isEmpty(item?.label) && convertStringToLowercase(item?.label) === 'search' ? (
                    <a href="#" className="toggle-dropdown search_toggle" id="search_toggle">
                      Search
                    </a>
                  ) : (
                    <Link
                      to={item?.url || '#'}
                      id={
                        !isEmpty(item?.label) && convertStringToLowercase(item?.label) === 'profile'
                          ? 'user-login'
                          : !isEmpty(item?.label) && convertStringToLowercase(item?.label) === 'cart'
                          ? 'cart'
                          : ''
                      }
                      // onMouseEnter={
                      //   !isEmpty(item?.label) &&
                      //   convertStringToLowercase(item?.label) === 'cart' &&
                      //   onExpandedMenuMouseEnter
                      // }
                      // onMouseLeave={onExpandedMenuMouseLeave}
                      onClick={(e) =>
                        onExpandedMenuClick(
                          e,
                          !isEmpty(item?.label) && convertStringToLowercase(item?.label) === 'profile'
                            ? appData?.customerData
                              ? ACCOUNT_ORDERS_PAGE
                              : LOGIN_PAGE
                            : item?.url || '#'
                        )
                      }
                    >
                      {!isEmpty(item?.label) && convertStringToLowercase(item?.label) === 'profile'
                        ? appData?.customerData
                          ? profileText
                          : loginText
                        : item?.title || ''}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {!isEmpty(cumulativePathSegments) && cumulativePathSegments?.length > 1 && (
          <nav aria-label="breadcrumb">
            <ul className="breadcrumbs">
              {cumulativePathSegments?.map((item, index) => {
                return (
                  <li key={item?.path || index}>
                    <a>{item?.label}</a>
                  </li>
                );
              })}
            </ul>
          </nav>
        )}

        <CartDropdown showCartDropdown={showCartDropdown} setShowCartDropdown={setShowCartDropdown} />

        <p className="header__follow">
          {followUsText}

          {!isEmpty(instagramUrl) && (
            <a href={instagramUrl} target="instagram" rel="noreferrer noopener">
              <span className="sr-only">{followUsOnInstagramText}</span>
              <InstagramIcon />
            </a>
          )}

          {!isEmpty(facebookUrl) && (
            <a href={facebookUrl} target="facebook" rel="noreferrer noopener">
              <span className="sr-only">{followUsOnFacebookText}</span>
              <FacebookIcon />
            </a>
          )}

          {!isEmpty(twitterUrl) && (
            <a href={twitterUrl} target="twitter" rel="noreferrer noopener">
              <span className="sr-only">{followUsOnTwitterText}</span>
              <TwitterIcon />
            </a>
          )}

          {!isEmpty(pinterestUrl) && (
            <a href={pinterestUrl} target="pinterest" rel="noreferrer noopener">
              <span className="sr-only">{followUsOnPinterestText}</span>
              <PinterestIcon />
            </a>
          )}
        </p>
      </div>
    </div>
  );
});
